import { HTMLAttributes } from "react"
import { useTranslation } from "react-i18next"
import { Check, Swap } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { joinStrings } from "@/lib/joinString"
import { cn } from "@/lib/utils"
import { CareProviderIcon } from "@/modules/care-provider/components/care-provider-icon"
import { combineConditionalCareProviderTitle } from "@/modules/care-provider/utils/combine-care-provider-title"
import { getCareProviderRelationName } from "@/modules/care-provider/utils/get-care-provider-relation-name"
import { useBetterDocSuggestionsStoreContext } from "@/modules/patients/detail/better-doc/store/useBetterDocSuggestionsStore"
import type { BetterDocCareProvider } from "@/modules/patients/detail/better-doc/types"

interface BetterDocSuggestionsProps {
  selectedId?: string
  progressNoteId?: string
  onSelectCareProvider?: (careProvider: GraphNode) => void
  onSetBetterDocSuggestion?: () => void
}

const SuggestionItem = (
  props: {
    item: BetterDocCareProvider
  } & BetterDocSuggestionsProps,
) => {
  const { t } = useTranslation()
  const { item, selectedId, onSelectCareProvider } = props
  const title = combineConditionalCareProviderTitle(item)
  const postalCity = joinStrings([item.postalCode, item.city], " ")
  const countryName = item.country?.countryName
  const address = joinStrings([item.streetAddress, postalCity, countryName], ", ")
  const isSelected = item.id === selectedId

  const conditionalProps: HTMLAttributes<HTMLDivElement> = {
    role: isSelected ? undefined : "button",
    tabIndex: isSelected ? -1 : undefined,
    onClick: isSelected ? undefined : () => onSelectCareProvider?.(item),
  }

  const textClass = isSelected ? "text-on-primary-surface" : "text-on-surface"
  const containerClass = isSelected ? "bg-primary-surface" : "hover:bg-primary-surface"

  const firstSpecialty = item.careProviderSpecialtyTranslations?.[0]?.name
  const relation = getCareProviderRelationName(item, t)

  return (
    <div className={cn("w-full cursor-pointer rounded-xl py-1", containerClass)} {...conditionalProps}>
      <div className="group grid grid-cols-[2rem_minmax(0,2fr)_minmax(0,auto)] items-center gap-4 p-2">
        <div className="group/assigned bg-decorative-plum-surface text-on-decorative-plum-surface text-subtitle-sm relative flex h-10 w-10 min-w-10 cursor-default items-center justify-center rounded-full select-none">
          <CareProviderIcon groupType={item.careProviderGroup.groupType} />
        </div>
        <div className="grid">
          <span className={cn("truncate", textClass)}>{title}</span>
          <span className={cn("text-primary truncate text-xs", textClass)}>{relation}</span>
          <span className={cn("truncate text-xs", textClass)}>
            {joinStrings([firstSpecialty, item.groupName], ", ")}
          </span>
          <span className={cn("truncate text-xs", textClass)}>{address}</span>
        </div>
        <div className="flex">
          <span className="ml-auto inline-flex items-center gap-1 px-2">
            {isSelected && <Check className="fill-on-primary-surface" />}
          </span>
        </div>
      </div>
    </div>
  )
}

const BetterDocSuggestions = ({
  selectedId,
  progressNoteId,
  onSelectCareProvider,
  onSetBetterDocSuggestion,
}: BetterDocSuggestionsProps) => {
  const { t } = useTranslation()
  const suggestions = useBetterDocSuggestionsStoreContext((store) => store.getBetterDocSuggestions)(progressNoteId)

  if (!suggestions || suggestions.length === 0) {
    return null
  }

  const handleSelectBetterDocCareProvider = (careProvider: GraphNode) => {
    onSelectCareProvider?.(careProvider)
    onSetBetterDocSuggestion?.()
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="circle" className="h-10 w-10 transition-all">
          <Swap />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="mt-0.5 -mr-12.5 rounded-lg pb-2" align="end">
        <div className="w-118">
          <div className="text-on-surface-subdued items-end px-4 py-3 text-sm leading-5 font-medium" role="none">
            {t("episodeOfCare.progressNote.pointOfCare.betterDocSuggestions")}
          </div>
          {suggestions.map((betterDoc) => (
            <DropdownMenuItem key={betterDoc.id} className="w-full">
              {betterDoc.__typename === "CareProvider" && (
                <SuggestionItem
                  item={betterDoc}
                  selectedId={selectedId}
                  onSelectCareProvider={handleSelectBetterDocCareProvider}
                />
              )}
            </DropdownMenuItem>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export { BetterDocSuggestions }
